import { useEffect, useMemo, useState } from "react";
import api, { fmtBRL, fmtNum, fmtInt } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Search } from "lucide-react";

export default function Inventory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | negative | zero | ok

  const load = () => {
    setLoading(true);
    api.get("/inventory", { params: { limit: 5000 } }).then(r => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return items.filter((i) => {
      if (s && !(i.name || "").toLowerCase().includes(s) && !(i.code || "").toString().includes(s)) return false;
      const qty = Number(i.quantity) || 0;
      if (filter === "negative" && qty >= 0) return false;
      if (filter === "zero" && qty !== 0) return false;
      if (filter === "ok" && qty <= 0) return false;
      return true;
    });
  }, [items, search, filter]);

  const counts = useMemo(() => {
    let neg = 0, zero = 0, ok = 0, totalValue = 0;
    items.forEach(i => {
      const q = Number(i.quantity) || 0;
      totalValue += Number(i.total_value) || (q * (Number(i.unit_cost) || 0));
      if (q < 0) neg++;
      else if (q === 0) zero++;
      else ok++;
    });
    return { neg, zero, ok, totalValue };
  }, [items]);

  return (
    <div data-testid="inventory-page">
      <PageHeader
        eyebrow="Estoque"
        title="POSIÇÃO ATUAL"
        accent="o que temos hoje."
        description="Saldo consolidado do CMC com identificação de itens em ruptura ou negativados."
        testId="inventory-header"
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="ifl-card p-5">
          <div className="label-caps">Valor total</div>
          <div className="font-display text-2xl mt-2">{fmtBRL(counts.totalValue)}</div>
        </div>
        <div className="ifl-card p-5">
          <div className="label-caps">Itens com saldo</div>
          <div className="font-display text-2xl mt-2">{fmtInt(counts.ok)}</div>
        </div>
        <div className="ifl-card p-5">
          <div className="label-caps">Zerados</div>
          <div className="font-display text-2xl mt-2" style={{ color: "var(--ifl-mustard)" }}>{fmtInt(counts.zero)}</div>
        </div>
        <div className="ifl-card p-5">
          <div className="label-caps">Negativos</div>
          <div className="font-display text-2xl mt-2" style={{ color: "var(--ifl-danger)" }}>{fmtInt(counts.neg)}</div>
        </div>
      </div>

      <div className="ifl-card p-4 mb-6 flex flex-col md:flex-row gap-3 items-center">
        <div className="flex items-center gap-2 flex-1 w-full">
          <Search size={16} strokeWidth={1.5} style={{ color: "var(--ifl-muted)" }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome ou código..."
            className="ifl-input flex-1 border-0 focus:outline-none"
            style={{ border: 0, padding: "0.5rem 0.25rem" }}
            data-testid="inventory-search"
          />
        </div>
        <div className="flex gap-2">
          {[
            { v: "all", l: "Todos" },
            { v: "ok", l: "Com saldo" },
            { v: "zero", l: "Zerados" },
            { v: "negative", l: "Negativos" },
          ].map(({ v, l }) => (
            <button
              key={v}
              onClick={() => setFilter(v)}
              className={filter === v ? "ifl-btn" : "ifl-btn-outline"}
              data-testid={`inventory-filter-${v}`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      <div className="ifl-card overflow-hidden">
        {loading ? (
          <div className="p-12 text-center font-body" style={{ color: "var(--ifl-muted)" }}>Carregando…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="ifl-table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Descrição</th>
                  <th className="text-right">Quantidade</th>
                  <th>Unidade</th>
                  <th className="text-right">CMC Unit.</th>
                  <th className="text-right">Valor Total</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, 800).map((i) => {
                  const q = Number(i.quantity) || 0;
                  const status = q < 0 ? { c: "var(--ifl-danger)", l: "Negativo" } : q === 0 ? { c: "var(--ifl-mustard)", l: "Zerado" } : { c: "var(--ifl-success)", l: "OK" };
                  return (
                    <tr key={i.id || `${i.code}-${i.name}`} data-testid={`inventory-row-${i.code}`}>
                      <td className="font-display text-xs">{i.code}</td>
                      <td>{i.name}</td>
                      <td className="text-right">{fmtNum(q, q % 1 === 0 ? 0 : 3)}</td>
                      <td className="font-display text-xs uppercase" style={{ color: "var(--ifl-muted)" }}>{i.unit}</td>
                      <td className="text-right">{fmtBRL(i.unit_cost)}</td>
                      <td className="text-right">{fmtBRL(i.total_value || q * (i.unit_cost || 0))}</td>
                      <td>
                        <span className="inline-flex items-center gap-2">
                          <span className="ifl-status-dot" style={{ background: status.c }} />
                          <span className="font-display text-xs uppercase tracking-widest">{status.l}</span>
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center py-12" style={{ color: "var(--ifl-muted)" }}>Nenhum item encontrado.</td>
                  </tr>
                )}
              </tbody>
            </table>
            {filtered.length > 800 && (
              <div className="px-4 py-3 text-xs font-body" style={{ color: "var(--ifl-muted)" }}>
                Exibindo 800 de {fmtInt(filtered.length)}. Refine a busca para encontrar itens específicos.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
